import { push } from 'redux-first-history';
import { IMethod, IRequestParams, IResponse } from '@dash/apiInstance';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import { sendRequest } from 'src/helpers/saga';
import { t } from 'src/i18n';
import { executeAction } from 'src/helpers/api';
import { logger } from 'src/helpers/logger';
import { store } from 'src/redux/store';
import notifications from '../../../redux/notifications/actions';
import actionTypes from './actionTypes';
import { ProcessActionRequestAction } from './actions.d';
import {
  IProcessActionResponse,
  IUpdateAgencyContract,
  PositionsErrors,
  Errors,
  IAmendmentData,
  IDistrictAttribute,
  IPositionAttribute,
} from './types.d';
import { VERSIONING_ACTIONS, resolveVersionedAgency } from '../viewAgency/contractVersioning/helper';
import agencyActions from '../viewAgency/actions';

function* processAction({ payload }: ProcessActionRequestAction<{}, {}>) {
  try {
    const { url, method, data, id, key } = payload;

    const params: { url?: string; method?: IMethod; data?: any } = { url, method };

    if (method?.toLowerCase() !== 'get') {
      params.data = data;
    }

    const result: IResponse<IProcessActionResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      executeAction,
      params,
      notifications.REQUEST_ERROR,
    );

    if ([200, 201].includes(result?.status)) {
      switch (true) {
        case key === 'validate_data_amendment': {
          const responseData = (result?.data?.success?.data || result?.data) as { changing_booked?: boolean };

          const changingBooked = responseData.changing_booked;

          if (changingBooked) {
            const errors: Errors = {};
            const amendmentData = data as IAmendmentData | undefined;

            if (amendmentData?.amendment?.districts_attributes) {
              amendmentData.amendment.districts_attributes.forEach((districtAttr: IDistrictAttribute) => {
                if (districtAttr.positions_attributes && districtAttr.positions_attributes.length > 0) {
                  const districtId = districtAttr.original_district_id;

                  if (!errors[districtId]) {
                    errors[districtId] = {
                      original_district_id: districtId,
                      positions: {},
                    };
                  }

                  districtAttr.positions_attributes.forEach((positionAttr: IPositionAttribute) => {
                    const positionId = positionAttr.id;

                    if (!errors[districtId].positions![positionId]) {
                      errors[districtId].positions![positionId] = {
                        id: positionId,
                      };
                    }

                    errors[districtId].positions![positionId].validation = t(
                      'dash.agencies.update_contract.changing_booked_warning',
                    );
                  });
                }
              });

              yield put({
                type: actionTypes.ON_VALIDATE_POSITION_ERROR,
                payload: { errors },
              });
            }
          } else {
            yield put({ type: actionTypes.ON_VALIDATE_POSITION_SUCCESS });
          }

          break;
        }
        case key === 'form_data_amendment':
          yield put({ type: actionTypes.UPDATE_FORM_INFO, payload: { data: result.data?.success?.data } });

          break;
        case key === 'create_amendment':
          yield put({ type: actionTypes.PROCESS_ACTION_SUCCESS });
          yield put(
            notifications.addNotification({
              headline: t('dash.common.notification.success_headline'),
              type: 'success',
              message: t('dash.common.notification.updated_successfully', {
                item: t('dash.agencies.update_contract.agency_contract_label'),
              }),
            }),
          );
          yield put(push(`/agencies/active/${id}?contractType=future_agency_show`));

          break;
        // @ts-ignore
        case VERSIONING_ACTIONS.includes(key): {
          const versionedAgency = result.data?.success?.data?.agency;
          const previousAgency = store.getState()?.AgencyView?.agency;

          const resolvedAgency = resolveVersionedAgency(versionedAgency, previousAgency);

          yield put({ type: actionTypes.UPDATE_AGENCY, payload: { agencyByType: resolvedAgency } });
          yield put({
            type: agencyActions.LOAD_SUCCESS,
            payload: {
              agency: {
                ...resolvedAgency,
                // Keep the base contract's id so "Edit this contract" stays loadable — the
                // fetched version has no contractId and its id isn't resolvable as a contract.
                contractId: previousAgency?.contractId ?? versionedAgency?.contractId,
              },
            },
          });

          break;
        }
        default:
          yield put(
            notifications.addNotification({
              headline: t('dash.common.notification.success_headline'),
              type: 'success',
              message: result?.messages?.join(' ') || '',
            }),
          );
      }
    } else {
      if (key === 'create_amendment') {
        const validationErrors = Object.entries(result || {});

        const baseErrorMessage: string[] = [];
        const districtNames: string[] = [];

        const positionsErrors: PositionsErrors = [];

        const updateAgencyContractData: IUpdateAgencyContract = store.getState()?.UpdateAgencyContract;
        const { formInfo, editForm } = updateAgencyContractData;

        const districts = formInfo?.states
          .filter(({ id: stateId }) => editForm?.operating_states?.value?.includes(stateId))
          .flatMap(filteredState => filteredState.districts)
          .filter(district => district && district.positions.length);

        validationErrors.forEach(([attr, error]) => {
          if (attr === 'base') {
            baseErrorMessage.push(error);
          }

          if (attr === 'disabling_booked_district_ids') {
            // @ts-ignore
            error.forEach(errorDistrictId => {
              const district = districts?.find(({ id: districtId }) => districtId === errorDistrictId);

              if (district) {
                districtNames.push(district.name);
              }
            });
          }

          if (attr === 'districts') {
            // @ts-ignore
            Object.values(error).forEach(({ original_district_id, positions }) => {
              const district = districts?.find(({ id: districtId }) => districtId === original_district_id);

              // @ts-ignore
              Object.values(positions).forEach(({ base, id: positionId }) => {
                let errorMessage = base;

                if (district) {
                  const position = district.positions.find(pos => pos.id === positionId);

                  if (position) {
                    errorMessage = `${errorMessage} ${t('dash.agencies.viewAgency.pricing_columns.position_title')}: ${
                      position.name
                    }`;
                  }
                }

                positionsErrors.push({
                  positionId,
                  districtId: original_district_id,
                  error: errorMessage,
                });
              });
            });
          }
        });

        let districtErrorMessage = null;

        if (baseErrorMessage) {
          districtErrorMessage = [...baseErrorMessage, districtNames.join(', ')].join(' ');
        }

        if (positionsErrors.length) {
          yield* positionsErrors.map(({ error }) =>
            put(
              notifications.addNotification({
                headline: t('dash.common.notification.error_headline'),
                type: 'error',
                message: error,
              }),
            ),
          );
        }

        if (districtErrorMessage) {
          yield put(
            notifications.addNotification({
              headline: t('dash.common.notification.error_headline'),
              type: 'error',
              message: districtErrorMessage,
            }),
          );
        }

        yield put({
          type: actionTypes.ON_CREATE_CONTRACT_ERROR,
          payload: { data: result, districtErrorMessage },
        });
      } else if (key === 'validate_data_amendment') {
        const errorData =
          (result as any)?.response || (result as any)?.data?.errors?.data?.validations || result?.data || result || {};

        const validationErrors = Array.isArray(errorData) ? errorData : Object.entries(errorData);

        const errors: Errors = {};
        let baseErrorMessage: string[] = [];
        const errorDistrictIds: string[] = [];

        const validatedPositionIds: { districtId: string; positionId: string }[] = [];

        // @ts-ignore
        if (data?.amendment?.districts_attributes) {
          // @ts-ignore
          data.amendment.districts_attributes.forEach((districtAttr: any) => {
            if (districtAttr.positions_attributes) {
              // @ts-ignore
              districtAttr.positions_attributes.forEach((positionAttr: any) => {
                validatedPositionIds.push({
                  districtId: districtAttr.original_district_id,
                  positionId: positionAttr.id,
                });
              });
            }
          });
        }

        validationErrors.forEach((entry: any) => {
          const [attr, error] = Array.isArray(entry) ? entry : [entry[0], entry[1]];

          if (attr === 'base') {
            // @ts-ignore - error is an array of error messages
            baseErrorMessage = Array.isArray(error) ? error : [error];
          }

          if (attr === 'disabling_booked_district_ids') {
            // @ts-ignore - error is an array of district IDs
            if (Array.isArray(error)) {
              errorDistrictIds.push(...error);
            }
          }
        });

        if (baseErrorMessage.length > 0 && validatedPositionIds.length > 0) {
          validatedPositionIds.forEach(({ districtId, positionId }) => {
            if (!errors[districtId]) {
              errors[districtId] = {
                original_district_id: districtId,
                positions: {},
              };
            }

            if (!errors[districtId].positions![positionId]) {
              errors[districtId].positions![positionId] = {
                id: positionId,
              };
            }
            // @ts-ignore - validation property exists on PositionError
            errors[districtId].positions![positionId].validation = baseErrorMessage.join(' ');
          });
        }

        yield put({
          type: actionTypes.ON_VALIDATE_POSITION_ERROR,
          payload: { errors },
        });
      } else {
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.error_headline'),
            type: 'error',
            message: t('dash.common.notification.something_went_wrong'),
          }),
        );
      }

      yield put({ type: actionTypes.PROCESS_ACTION_ERROR });
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actionTypes.PROCESS_ACTION_REQUEST, processAction)]);
}
